import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Support module client-side API: Linear OAuth status, connect, disconnect,
 * team metadata, ticket creation / listing / detail, and the attachment
 * sign-upload helper.
 */

export interface LinearAuthStatus {
  connected: boolean;
  linearUserId?: string;
  linearUserEmail?: string;
  linearWorkspaceId?: string;
  scope?: string;
  expiresAt?: string;
}

const SUPPORT_AUTH_QUERY_KEY = ['Support', 'LinearAuthStatus'] as const;

/**
 * Read whether the current user has a stored Linear OAuth token.
 */
export function useLinearAuthStatus() {
  const apiClient = useApiClient();

  return useQuery<LinearAuthStatus>({
    queryKey: SUPPORT_AUTH_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: LinearAuthStatus;
      }>('/support/auth/linear/status');
      return response.data;
    },
  });
}

/**
 * Kick off the OAuth flow: server returns the authorize URL with a signed
 * state cookie pinned. Caller should navigate the browser to the returned
 * URL (full-page redirect, not a new window — the cookie is HttpOnly +
 * SameSite=Lax, so a same-origin top-level navigation is the right pattern).
 */
export function useStartLinearAuth() {
  const apiClient = useApiClient();

  return useMutation<{ authorizeUrl: string }, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: { authorizeUrl: string };
      }>('/support/auth/linear/start');
      return response.data;
    },
  });
}

/**
 * Disconnect the current user's Linear integration. Soft-deletes the row
 * server-side and best-effort revokes the access token at Linear.
 */
export function useDisconnectLinear() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiClient.delete('/support/auth/linear');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_AUTH_QUERY_KEY });
    },
  });
}

// ─── Team metadata ────────────────────────────────────────────────────────

export interface SupportTeamState {
  id: string;
  name: string;
  type: string;
  color: string;
}
export interface SupportTeamLabel {
  id: string;
  name: string;
  color: string;
}
export interface SupportTeam {
  id: string;
  key: string;
  name: string;
  states: SupportTeamState[];
  labels: SupportTeamLabel[];
}

const SUPPORT_TEAM_QUERY_KEY = ['Support', 'Team'] as const;

export function useSupportTeam(enabled = true) {
  const apiClient = useApiClient();
  return useQuery<SupportTeam>({
    queryKey: SUPPORT_TEAM_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: SupportTeam;
      }>('/support/team');
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Attachments ──────────────────────────────────────────────────────────

export interface TicketAttachment {
  assetUrl: string;
  filename: string;
  contentType: string;
}

/**
 * Upload an attachment for a support ticket.
 *
 * The file is POSTed (multipart) to tours-api, which signs the upload and
 * streams the bytes to Linear server-side. We deliberately do NOT PUT to
 * Linear from the browser — Linear's storage host blocks cross-origin browser
 * PUTs, which surfaced as a `Failed to fetch` error (SUP-13).
 */
export function useUploadAttachment() {
  const apiClient = useApiClient();
  return useMutation<TicketAttachment, Error, File>({
    mutationFn: async file => {
      const form = new FormData();
      form.append('file', file);
      const response = await apiClient.post<{
        success: boolean;
        data: TicketAttachment;
      }>('/support/uploads', form);
      return response.data;
    },
  });
}

// ─── Tickets ──────────────────────────────────────────────────────────────

/** Discriminated-union of the possible ticket-create payloads. Mirror of
 *  the Zod schemas in the API. */
export type CreateTicketInput =
  | (TicketBaseFields & {
      type: 'bug';
      area: string;
      stepsToReproduce: string;
      expected: string;
      actual: string;
      frequency: 'always' | 'sometimes' | 'once';
      severity: 'blocker' | 'serious' | 'minor' | 'cosmetic';
      startedAt?: string;
      url?: string;
      userAgent?: string;
      viewport?: string;
      appVersion?: string;
    })
  | (TicketBaseFields & {
      type: 'feature';
      audience: 'guest' | 'hotel' | 'internal';
      problem: string;
      workaround?: string;
      acceptanceCriteria: string;
      priority: 'nice-to-have' | 'important' | 'urgent';
    })
  | (TicketBaseFields & {
      type: 'content-fix';
      entityKind: EntityKind;
      entityIdOrName: string;
      whatsWrong: string;
      whatItShouldSay: string;
      source_link?: string;
    })
  | (TicketBaseFields & {
      type: 'question';
      whatYoureTryingToDo: string;
      whatYouTried?: string;
      urgency: 'blocking' | 'soon' | 'whenever';
    })
  | (TicketBaseFields & {
      type: 'data-quality';
      entityKind: EntityKind;
      entityIdOrName: string;
      fieldName: string;
      expectedValue: string;
      visibility: 'guest-visible' | 'internal-only';
    });

export interface TicketBaseFields {
  title: string;
  description: string;
  attachments?: TicketAttachment[];
  source?: 'tours-client' | 'mcp';
}

export type EntityKind = 'attraction' | 'tour' | 'hotel' | 'neighborhood' | 'greater-area';

export interface CreatedTicket {
  id: string;
  identifier: string;
  url: string;
  number: number;
  title: string;
}

export interface TicketListEntry {
  id: string;
  identifier: string;
  title: string;
  url: string;
  priority: number;
  createdAt: string;
  updatedAt: string;
  state: SupportTeamState;
  labels: SupportTeamLabel[];
}

export interface TicketComment {
  id: string;
  body: string;
  createdAt: string;
  user: { id: string; name: string; email: string };
}

export interface TicketDetail extends TicketListEntry {
  description: string | null;
  comments: TicketComment[];
}

const SUPPORT_TICKETS_LIST_KEY = ['Support', 'Tickets', 'list'] as const;
const supportTicketDetailKey = (id: string) => ['Support', 'Tickets', 'detail', id] as const;

export function useMyTickets() {
  const apiClient = useApiClient();
  return useQuery<TicketListEntry[]>({
    queryKey: SUPPORT_TICKETS_LIST_KEY,
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: TicketListEntry[];
      }>('/support/tickets');
      return response.data;
    },
  });
}

export function useTicket(id: string | undefined) {
  const apiClient = useApiClient();
  return useQuery<TicketDetail>({
    queryKey: id ? supportTicketDetailKey(id) : ['Support', 'Tickets', 'detail', 'none'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: TicketDetail;
      }>(`/support/tickets/${id}`);
      return response.data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateTicket() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CreatedTicket, Error, CreateTicketInput>({
    mutationFn: async input => {
      const response = await apiClient.post<{
        success: boolean;
        data: CreatedTicket;
      }>('/support/tickets', input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_LIST_KEY });
    },
  });
}
